import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { PartyPopper, Building2, Smartphone, Film } from 'lucide-react';

const services = [
  {
    icon: PartyPopper,
    title: 'Personal Events',
    price: 'Starting at $300',
    shortDesc: 'Designed to preserve emotion, perspective, and the full experience of your moment.',
    description: 'Some moments are meant to be fully experienced, not divided between living them and trying to capture them. Alita Captures approaches personal events with intention, focusing not only on what is happening, but on how it feels in the moment.',
    includes: [
      'Event coverage',
      'Cinematic edited video',
      'Color grading',
      'Ready-to-share content'
    ],
  },
  {
    icon: Building2,
    title: 'Business Events',
    price: 'Starting at $400',
    shortDesc: 'Designed to capture your event and create content that continues to serve your brand.',
    description: 'When running an event, the focus should remain on execution, not documentation. Alita Captures captures each event with a structured and intentional approach, ensuring that key moments, details, and audience interactions are preserved without disrupting the experience.',
    includes: [
      'Event coverage',
      'Edited recap video',
      'Reusable content clips',
      'Social media-ready content'
    ],
  },
  {
    icon: Smartphone,
    title: 'Social Media Content',
    price: 'Starting at $350',
    shortDesc: 'Designed to help you stay consistent and visible without managing content creation yourself.',
    description: 'Maintaining a consistent and professional presence online requires time, planning, and execution. Alita Captures provides ready-to-use content that supports your visibility without adding to your workload.',
    includes: [
      'Filming session',
      'Multiple short-form clips',
      'Edited videos (Reels/TikTok)',
      'Optimized, ready-to-post content'
    ],
  },
  {
    icon: Film,
    title: 'Filming-Only Option',
    price: 'Available Upon Request',
    shortDesc: 'For clients who require professional capture without full content delivery.',
    description: 'Filming-only services can be discussed through inquiry for clients who prefer to handle their own post-production or have specific delivery requirements.',
    includes: [
      'Professional filming',
      'Raw footage delivery',
      'Custom arrangements available'
    ],
  },
];

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="services"
      className="py-24 md:py-32 bg-cream"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="font-sans text-xs tracking-superwide text-terracotta mb-4">
            SERVICES
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-charcoal mb-6">
            Intentional Content, Built Around Your Needs
          </h2>
          <p className="font-body text-base md:text-lg text-stone max-w-3xl mx-auto leading-relaxed">
            Every project is approached with care, structure, and intention. Alita Captures handles the full content process, from capture to final delivery, creating visuals that can be relived, shared, and continue to serve beyond the moment itself.
          </p>
        </motion.div>

        {/* Services List */}
        <div className="space-y-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="border border-sand bg-cream/50 p-8 md:p-12"
              data-testid={`service-${index}`}
            >
              <div className="grid md:grid-cols-3 gap-8">
                {/* Left: Title & Price */}
                <div className="md:border-r border-sand md:pr-8">
                  <service.icon
                    size={32}
                    className="text-terracotta mb-4"
                  />
                  <h3 className="font-serif text-2xl md:text-3xl text-charcoal mb-2">
                    {service.title}
                  </h3>
                  <p className="font-sans text-sm tracking-widest text-terracotta">
                    {service.price}
                  </p>
                </div>

                {/* Center: Description */}
                <div className="md:col-span-1">
                  <p className="font-body text-base text-charcoal font-medium mb-4">
                    {service.shortDesc}
                  </p>
                  <p className="font-body text-sm text-stone leading-relaxed">
                    {service.description}
                  </p>
                </div>

                {/* Right: Includes */}
                <div>
                  <p className="font-sans text-xs tracking-widest text-charcoal mb-4">
                    INCLUDES:
                  </p>
                  <ul className="space-y-2">
                    {service.includes.map((item, i) => (
                      <li key={i} className="font-body text-sm text-stone flex items-start gap-2">
                        <span className="text-terracotta mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    onClick={scrollToContact}
                    className="inline-block mt-6 font-sans text-xs tracking-widest text-terracotta hover:text-charcoal transition-colors duration-300"
                  >
                    INQUIRE ABOUT THIS SERVICE →
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Final Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 text-center border-t border-sand pt-12"
        >
          <p className="font-body text-base text-stone italic">
            Every project is tailored. Final pricing is confirmed after consultation based on scope, duration, and location.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;

